import { logger } from '@/lib/logger';
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ObliqueIcon from '@/components/icons/ObliqueIcon';
import { useNavigate } from 'react-router-dom';
import Logo from '@/components/Logo';
import { Button } from '@/components/ui/button';

export default function ContactPage() {
    const navigate = useNavigate();
    const [isChatOpen, setIsChatOpen] = useState(false);
    const [formStatus, setFormStatus] = useState('idle');

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setFormStatus('submitting');

        const formData = new FormData(e.target);
        const data = Object.fromEntries(formData.entries());

        try {
            await fetch('https://formsubmit.co/ajax/oblique@eleventh.tech', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({
                    _subject: `New Lead: ${data.name} from ${data.companyName}`,
                    _template: 'table',
                    "Full Name": data.name,
                    "Phone Number": data.phone,
                    "Email Address": data.email,
                    "Company Name": data.companyName,
                    "Company Size": data.companySize,
                    "Current CRM": data.crm,
                    "Reason for Use": data.reason
                })
            });
            setFormStatus('success');
            e.target.reset();
            setTimeout(() => setFormStatus('idle'), 5000);
        } catch (error) {
            logger.error('Submission error', error);
            setFormStatus('error');
        }
    };

    return (
        <div style={{ background: 'var(--bg-page)', minHeight: '100vh', color: 'var(--text-primary)' }}>

            {/* Minimal Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '24px 40px', borderBottom: '1px solid var(--border)', background: 'var(--bg-surface)' }}>
                <div onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
                    <Logo size={120} />
                </div>
                <div style={{ display: 'flex', gap: '32px', fontSize: '14px', fontWeight: '500', color: 'var(--text-secondary)' }}>
                    <div onClick={() => navigate('/software')} style={{ cursor: 'pointer' }} className="hover:text-[var(--text-primary)]">Software</div>
                    <div onClick={() => navigate('/solutions')} style={{ cursor: 'pointer' }} className="hover:text-[var(--text-primary)]">Solutions</div>
                    <div onClick={() => navigate('/resources')} style={{ cursor: 'pointer' }} className="hover:text-[var(--text-primary)]">Resources</div>
                    <div onClick={() => navigate('/pricing')} style={{ cursor: 'pointer' }} className="hover:text-[var(--text-primary)]">Pricing</div>
                </div>
                    <Button onClick={() => navigate('/login')} variant="ghost" className="hover:text-accent font-medium">
                        Log in
                    </Button>
            </div>

            <div style={{ padding: '80px 40px' }}>
                <div style={{ maxWidth: '800px', margin: '0 auto' }}>
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                        style={{ fontSize: '48px', fontWeight: '600', marginBottom: '24px', letterSpacing: '-0.02em' }}
                    >
                        Contact Sales & Support
                    </motion.h1>
                    <p style={{ fontSize: '20px', color: 'var(--text-secondary)', marginBottom: '60px', lineHeight: 1.6 }}>
                        Whether you are an existing customer or looking to protect your sales floor's revenue, our team is ready to assist.
                    </p>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '24px', marginBottom: '60px' }}>
                        <div style={{ padding: '32px', background: 'var(--bg-surface)', borderRadius: '16px', border: '1px solid var(--border)' }}>
                            <ObliqueIcon name="mail" style={{ color: 'var(--accent)', width: '32px', height: '32px', marginBottom: '16px' }} />
                            <h3 style={{ fontSize: '20px', fontWeight: '500', marginBottom: '8px' }}>Email Us</h3>
                            <p style={{ color: 'var(--text-tertiary)', marginBottom: '16px' }}>Responses within 2 business hours.</p>
                            <a href="mailto:oblique@eleventh.tech" style={{ color: 'var(--text-primary)', fontWeight: '500', textDecoration: 'none' }}>oblique@eleventh.tech</a>
                        </div>

                        <div style={{ padding: '32px', background: 'var(--bg-surface)', borderRadius: '16px', border: '1px solid var(--border)' }}>
                            <ObliqueIcon name="chat" style={{ color: 'var(--blue)', width: '32px', height: '32px', marginBottom: '16px' }} />
                            <h3 style={{ fontSize: '20px', fontWeight: '500', marginBottom: '8px' }}>Live Chat</h3>
                            <p style={{ color: 'var(--text-tertiary)', marginBottom: '16px' }}>Talk directly to our implementation team.</p>
                            <button onClick={() => setIsChatOpen(true)} style={{ background: 'transparent', border: 'none', color: 'var(--text-primary)', fontWeight: '500', padding: 0, cursor: 'pointer' }}>Open Support Chat</button>
                        </div>
                    </div>

                    <form onSubmit={handleFormSubmit} style={{ background: 'var(--bg-surface)', borderRadius: '24px', padding: '40px', border: '1px solid var(--border)' }}>
                        <h3 style={{ fontSize: '24px', fontWeight: '500', marginBottom: '24px' }}>Send a Message</h3>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                            <div>
                                <label style={{ display: 'block', fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '8px' }}>Full Name</label>
                                <input name="name" required type="text" style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-page)', color: 'var(--text-primary)' }} />
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '8px' }}>Phone No.</label>
                                <input name="phone" required type="tel" style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-page)', color: 'var(--text-primary)' }} />
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                            <div>
                                <label style={{ display: 'block', fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '8px' }}>Work Email</label>
                                <input name="email" required type="email" style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-page)', color: 'var(--text-primary)' }} />
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '8px' }}>Company Name</label>
                                <input name="companyName" required type="text" style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-page)', color: 'var(--text-primary)' }} />
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                            <div>
                                <label style={{ display: 'block', fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '8px' }}>Company Size</label>
                                <select name="companySize" required defaultValue="" style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-page)', color: 'var(--text-primary)' }}>
                                    <option value="" disabled>Select Size</option>
                                    <option value="1-50">1-50 Employees</option>
                                    <option value="51-200">51-200 Employees</option>
                                    <option value="201-500">201-500 Employees</option>
                                    <option value="500+">500+ Employees</option>
                                </select>
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '8px' }}>Which CRM do you use?</label>
                                <select name="crm" required defaultValue="" style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-page)', color: 'var(--text-primary)' }}>
                                    <option value="" disabled>Select CRM</option>
                                    <option value="Salesforce">Salesforce</option>
                                    <option value="HubSpot">HubSpot</option>
                                    <option value="Zoho">Zoho</option>
                                    <option value="Pipedrive">Pipedrive</option>
                                    <option value="Microsoft Dynamics">Microsoft Dynamics</option>
                                    <option value="Other">Other</option>
                                    <option value="N/A">N/A</option>
                                </select>
                            </div>
                        </div>

                        <div style={{ marginBottom: '32px' }}>
                            <label style={{ display: 'block', fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '8px' }}>Reason for Use</label>
                            <textarea name="reason" required rows={4} style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-page)', color: 'var(--text-primary)', resize: 'vertical' }} />
                        </div>

                        <Button
                            disabled={formStatus === 'submitting'}
                            variant="default"
                            className="w-full sm:w-auto py-3 px-6 rounded-lg font-medium transition-opacity"
                            style={{ opacity: formStatus === 'submitting' ? 0.7 : 1 }}
                        >
                            {formStatus === 'submitting' ? 'Submitting...' : formStatus === 'success' ? 'Request Sent!' : 'Submit Request'}
                        </Button>
                        {formStatus === 'success' && <p style={{ color: 'var(--success)', marginTop: '12px', fontSize: '14px' }}>Your request has been sent successfully. We will be in touch shortly.</p>}
                    </form>
                </div>

                {/* Chat Overlay Widget */}
                <div style={{ position: 'fixed', bottom: '30px', right: '30px', zIndex: 1000, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '16px' }}>
                    <AnimatePresence>
                        {isChatOpen && (
                            <motion.div
                                initial={{ opacity: 0, y: 20, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.95 }}
                                style={{
                                    background: 'var(--bg-surface)', width: '350px', height: '500px',
                                    borderRadius: '24px', border: '1px solid var(--border)',
                                    boxShadow: '0 24px 48px rgba(0,0,0,0.1)',
                                    display: 'flex', flexDirection: 'column', overflow: 'hidden'
                                }}
                            >
                                <div style={{ padding: '24px', background: 'var(--text-primary)', color: 'var(--bg-page)', position: 'relative' }}>
                                    <h3 style={{ fontSize: '18px', fontWeight: '500', margin: 0, paddingRight: '40px' }}>Oblique Support</h3>
                                    <p style={{ fontSize: '13px', margin: '4px 0 0', opacity: 0.8 }}>We typically reply in minutes.</p>
                                    <Button onClick={() => setIsChatOpen(false)} variant="ghost" size="icon" style={{
                                        position: 'absolute', top: '16px', right: '16px', background: 'rgba(128,128,128,0.2)',
                                        borderRadius: '50%', color: 'var(--bg-page)',
                                        zIndex: 10
                                    }} className="hover:bg-[rgba(128,128,128,0.4)] h-8 w-8">
                                        <ObliqueIcon name="close" size={16} />
                                    </Button>
                                </div>
                                <div style={{ flex: 1, padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px', background: 'var(--bg-page)' }}>
                                    <div style={{ background: 'var(--bg-surface)', padding: '16px', borderRadius: '16px 16px 16px 4px', border: '1px solid var(--border)', maxWidth: '85%' }}>
                                        <p style={{ margin: 0, fontSize: '14px', lineHeight: 1.5, color: 'var(--text-primary)' }}>Hi there! Looking to protect your revenue string? How can we help today?</p>
                                    </div>
                                </div>
                                <div style={{ padding: '16px', borderTop: '1px solid var(--border)', background: 'var(--bg-surface)', display: 'flex', gap: '12px' }}>
                                    <input type="text" placeholder="Type a message..." style={{ flex: 1, padding: '12px 16px', borderRadius: '24px', border: '1px solid var(--border)', background: 'var(--bg-page)', color: 'var(--text-primary)', outline: 'none' }} />
                                    <Button size="icon" className="rounded-full bg-accent text-white h-11 w-11 flex-shrink-0">
                                        <ObliqueIcon name="send" size={18} style={{ transform: 'translateX(-1px)' }} />
                                    </Button>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {!isChatOpen && (
                        <motion.button
                            initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}
                            onClick={() => setIsChatOpen(true)}
                            style={{
                                width: '64px', height: '64px', borderRadius: '50%',
                                background: 'var(--text-primary)', color: 'var(--bg-page)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)', border: 'none', cursor: 'pointer',
                                outline: 'none', padding: 0
                            }}
                            className="hover:scale-110 transition-transform"
                        >
                            <ObliqueIcon name="chat" size={28} />
                        </motion.button>
                    )}
                </div>
            </div>
        </div>
    );
}
